import { ConstantsService } from "@services/constantsService";
import { StateRegistry, Transition } from "@uirouter/angularjs";
import { StateService } from "@uirouter/core";
import { IRegisterTab } from "./IRegisterTab";
import { IVeriTabBindings } from "./veriTab.component";
import "./veriTabs.scss";
import "./veriVerticalTabs.scss";

export class VeriTabsComponentController implements angular.IComponentController, IRegisterTab {
    tabs: IVeriTabBindings[] = [];

    selectedTabComponent: IVeriTabBindings;
    selectedTab: string;
    selectedTabIndex: number;

    nextTab: IVeriTabBindings;
    previousTab: IVeriTabBindings;

    hasResourceFilterBar: boolean;
    hasTitleEnd: boolean;
    hasTitleStart: boolean;

    stepper: boolean;
    slimStepper: boolean = false;
    stepperValidationError: string;
    stepperOffset: number;

    autoSize: boolean;
    onTabChanged: (args: { tabIndex: number, tabTitle: string, tabReference: number }) => void;
    onFinish: () => void;

    finishMessage: string;
    disableBack: boolean;
    disableNext: boolean;
    disableFinish: boolean;
    disableAutoTabSelect: boolean;
    hideNav: boolean = false;

    // ---

    processingNextButtonFunction: boolean = false;

    isMobile: boolean = false;

    static $inject = ["$q", "$element", "$transclude", "$state", "$stateRegistry", "$timeout", "$transitions", "$animateCss", "$mdUtil", "constantsService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $element: JQLite,
        private readonly $transclude: angular.ITranscludeFunction,
        private readonly $state: StateService,
        private readonly $stateRegistry: StateRegistry,
        private readonly $timeout: angular.ITimeoutService,
        private readonly $transitions: Transition,
        private readonly $animateCss: any,
        private readonly $mdUtil: any,
        private readonly constantsService: ConstantsService) {

        $transitions.onSuccess({}, (trans) => {
            trans.promise.then(result => {
                if (this.tabs.some(tab => !!tab.location)) {
                    this.reflectTransition(trans);
                }
                var pageName = this.$state.params["page"];
                if (pageName) {
                    this.showTabUrl(pageName);
                }
            });
        });
    }

    reflectTransition(trans: Transition) {
        const lastSegment = trans.router.urlService.path().split('/').pop();
        const tabToShow = this.tabs.find(tab =>  tab.location === lastSegment);

        !!tabToShow && this.showTab(tabToShow, false);
    }

    $onInit() {
        this.isMobile = this.constantsService.getIsMobile();
        this.hasResourceFilterBar = this.$transclude.isSlotFilled("resourceFilterBar");
        this.hasTitleStart = this.$transclude.isSlotFilled("veriTabsTitleStart");
        this.hasTitleEnd = this.$transclude.isSlotFilled("veriTabsTitleEnd");

        this.calculateStepperSize();
    }

    $onChanges(onChangesObj: angular.IOnChangesObject) {

        if (onChangesObj["selectedTab"] && this.selectedTab !== undefined) {
            const tab = this.findTab(this.selectedTab);

            if (tab) {
                this.showTab(tab);
            }
        }
    }

    registerTab(tab: IVeriTabBindings) {
        this.tabs.push(tab);
        this.tabs.sort((tab1, tab2) => tab1?.preferredOrder - tab2?.preferredOrder);
        this.updateSelectedTabIndex();

        if (this.$state.params["page"] && tab.url) {
            if (this.$state.params["page"] === tab.url) {
                this.showTab(tab, false);
            }
        }
        else if (!this.disableAutoTabSelect && (this.tabs.length === 1 || this.selectedTab == tab.tabTitle)) {
            this.showTab(tab, false);
        }

        this.updateNextPreviousTabs();

        this.calculateLeftyRighty();
        this.calculateStepperSize();
    }

    unregisterTab(tab: IVeriTabBindings) {
        var tabIndex = this.tabs.indexOf(tab);
        if (tabIndex >= 0) {
            this.tabs.splice(tabIndex, 1);
        }
    }

    tabVisibilityChanged(tab: IVeriTabBindings) {
        if (tab.tabVisible) {
            return;
        }

        var currentIndex = this.tabs.indexOf(tab);
        const nextTab = this.findTab(`${currentIndex + 1}`);
        this.showTab(nextTab);
    }

    showTabViaStepper(tabToShow: IVeriTabBindings, animate = true): void {
        const indexOfCurrentTab = this.tabs.indexOf(this.selectedTabComponent);
        const indexOfClickedTab = this.tabs.indexOf(tabToShow);

        if ((indexOfClickedTab === indexOfCurrentTab + 1 && (this.disableNext || this.stepperValidationError))
            || indexOfClickedTab > indexOfCurrentTab + 1) {
            return;
        }

        this.showTab(tabToShow, animate);
    }

    showTab(tabToShow: IVeriTabBindings, animate = true) {
        if (this.selectedTabComponent === tabToShow || !tabToShow) {
            return;
        }

        this.tabs.forEach(tab => {
            if (tab === tabToShow) {
                this.selectedTabComponent = tab;
                this.updateSelectedTabIndex();

                tab.show(animate);
                this.notifyTabChange(tab);
            }
            else {
                tab.hide(animate);
            }
        });

        this.updateNextPreviousTabs();

        this.calculateLeftyRighty();
        this.calculateStepperSize();

        if (tabToShow.url) {
            const transitionObject = { "page": tabToShow.url, option: null };

            this.$state.go(this.$state.current.name, transitionObject, { location: true, notify: false, reload: false });

            this.$state.current.params["subTitle"] = tabToShow.tabTitle;
        }
    }

    updateNextPreviousTabs() {
        this.previousTab = this.tabs[this.selectedTabIndex - 1];
        this.nextTab = this.tabs[this.selectedTabIndex + 1];
    }

    next() {
        const nextTabIndex = this.selectedTabIndex + 1;

        // TODO - revisit promise binding
        if (this.selectedTabComponent && this.selectedTabComponent.nextButtonFunction) {
            this.processingNextButtonFunction = true;

            const nextButtonPromise = this.selectedTabComponent.nextButtonFunction();

            this.$q.when(nextButtonPromise).then(() => {
                this.showTab(this.tabs[nextTabIndex]);
            }).finally(() => {
                this.processingNextButtonFunction = false;
            });
        }

        this.showTab(this.tabs[nextTabIndex]);
    }

    previous() {
        var prevTabIndex = this.selectedTabIndex - 1;
        this.showTab(this.tabs[prevTabIndex]);
    }

    finish() {
        if (this.onFinish) {
            this.onFinish();
        }
    }

    setContentHeight(height: number) {
        if (!this.autoSize) {
            return;
        }

        var container = this.$element.children(".veri-tab-container").first() as JQLite;

        if (height) {
            container[0].style["height"] = `${height}px`;
        }
        else {
            //container[0].style["height"] = null;
        }

        //console.log("setContentHeight", container, height);
        //console.log(this.$element);
        //this.$animateCss(this.$element, {
        //    from: 100,
        //    to: height,
        //    //easing: 'cubic-bezier(0.35, 0, 0.25, 1)',
        //    duration: 0.5
        //})
        //.start().done(function () {
        //    // Then (to fix the same iOS issue as above), disable transitions and remove the specific
        //    // pixel height so the height can size with browser width/content changes, etc.
        //    this.$element.css({
        //        transition: 'none',
        //        height: ''
        //    });

        //    // In the next tick, re-allow transitions (if we do it all at once, $element.css is "smart"
        //    // enough to batch it for us instead of doing it immediately, which undoes the original
        //    // transition: none)
        //    //this.$mdUtil.nextTick(function () {
        //    //    this.$element.css('transition', '');
        //    //});
        //});
    }

    // -------------------

    private updateSelectedTabIndex(): void {
        this.selectedTabIndex = this.tabs.indexOf(this.selectedTabComponent);
    }

    private showTabUrl(tabUrl: string) {
        this.tabs.forEach(tab => {
            if (tab.url && tab.url === tabUrl && tab !== this.selectedTabComponent) {
                this.showTab(tab, false);
            }
        });
    }

    private notifyTabChange(tabToShow: IVeriTabBindings) {
        const index = this.tabs.indexOf(tabToShow);

        if (this.onTabChanged) {
            this.onTabChanged({ tabIndex: index, tabTitle: tabToShow.tabTitle, tabReference: tabToShow.tabReference });
        }
    }

    private findTab(tabToFind: string) {
        var foundTab: IVeriTabBindings = null;
        const tabIndex = Number(tabToFind);
        if (!this.isNaN(tabIndex)) {
            foundTab = this.tabs[tabIndex];
        }

        if (!foundTab) {
            this.tabs.forEach(tab => {
                if (tab?.tabTitle?.toUpperCase() === tabToFind?.toUpperCase()) {
                    foundTab = tab;
                }
            });
        }

        return foundTab;
    }

    // IE11 doesnt have an isNan function
    private isNaN(o) {
        return typeof (o) === 'number' && isNaN(o);
    }

    private calculateLeftyRighty() {

        var hasSeenSelected = false;

        this.tabs.forEach(tab => {
            var stateChanged = tab.url ? "state-changed" : "";
            if (tab === this.selectedTabComponent) {
                hasSeenSelected = true;

                tab.setClassName(`is-active ${stateChanged}`);
            }
            else if (hasSeenSelected) {
                tab.setClassName(`is-right ${stateChanged}`);
            }
            else {
                tab.setClassName(`is-left ${stateChanged}`);
            }
        });
    }

    private calculateStepperSize(): void {
        if (!this.stepper || (!this.slimStepper && !this.isMobile)) {
            return;
        }

        this.$timeout(() => {
            const stepperBarListItems: HTMLCollection = this.$element.find(".stepper-bar")[0]?.children;
            const leftMargin = 18;

            for (var i = 0; i < stepperBarListItems.length; i++) {
                this.tabs[i].stepWidth = stepperBarListItems.item(i)?.clientWidth + leftMargin;
            }

            this.calculateStepperOffset();
        });
    }

    private calculateStepperOffset(): void {
        this.stepperOffset = 0;
    }
}

export class VeriTabsComponent implements angular.IComponentOptions {
    bindings: any;
    require: { [index: string]: string; };
    controller: any;
    template: string | angular.Injectable<(...args: any[]) => string>;
    transclude: boolean | { [index: string]: string; };

    constructor(
        vertical = false
    ) {
        this.bindings = {
            selectedTab: "<?",
            disableBack: "<?",
            disableNext: "<?",
            disableFinish: "<?",
            disableAutoTabSelect: "<?",
            hideNav: "<?",
            stepper: "<?",
            slimStepper: "<?",
            stepperValidationError: "<?",
            autoSize: "@",
            finishMessage: "@",
            onTabChanged: "&",
            onFinish: "&"
        };

        this.controller = VeriTabsComponentController;
        this.template = ($element: JQuery, $attrs: angular.IAttributes) => {

            if (vertical) {
                return require("./veriVerticalTabs.html");
            }

            return require("./veriTabs.html");
        }

        this.template.$inject = ["$element", "$attrs"];
        this.transclude = {
            "resourceFilterBar": "?resourceFilterBar",
            "veriTabsTitleEnd": "?veriTabsTitleEnd",
            "veriTabsTitleStart": "?veriTabsTitleStart",
        };
    }
}
